// Every .max() here was checked against the LIVE column width on 2026-08-12, because
// this file had drifted from the database in BOTH directions and each direction
// fails differently:
//
//   zod tighter than the column  -> the API rejects a save Postgres would have
//     accepted. This is the bug that keeps recurring (spray_template.reference,
//     spray_template.batch_number): the column gets widened, the validator doesn't,
//     and the save dies at the boundary with a length error nobody can act on.
//
//   zod looser than the column   -> the value sails through validation and Postgres
//     throws instead, so the user gets a 500 rather than a field-level message.
//
// If you widen a column, widen it here in the same change.
import { z } from 'zod';
import { RowLabelOptionalSchema } from './row-label';

export const IrrigationTaskSchema = z.object({
  id: z.number().int().positive(),
  company_id: z.string().uuid(),
  year: z.string().max(4).nullable().optional(),
  block_name: z.string().max(400).nullable().optional(),
  block_type: z.string().max(100).nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  crop_variety: z.string().max(150).nullable().optional(),
  total_hectares: z.number().nullable().optional(),
  ml_per_hour: z.number().nullable().optional(),
  rate_value: z.number().nullable().optional(),
  // Column is varchar(100), not 255/500 — these were letting oversized values
  // through to fail at Postgres. fertilizer_template holds a joined summary of the
  // selected template(s), so it is the most likely of the two to actually hit 100;
  // if it starts truncating in practice the COLUMN needs widening, not this cap.
  water_source: z.string().max(100).nullable().optional(),
  fertilizer_template: z.string().max(100).nullable().optional(),
  fertilizer_ids: z.string().nullable().optional(),
  ppe_used: z.string().nullable().optional(),
  // text column — ppe_used above is text for the same reason (widened 2026-08-11).
  weather: z.string().nullable().optional(),
  tempreature: z.string().max(100).nullable().optional(),
  humidity: z.string().max(100).nullable().optional(),
  windspeed: z.string().max(100).nullable().optional(),
  direction: z.string().max(100).nullable().optional(),
  start_time: z.string().nullable().optional(),
  finish_time: z.string().nullable().optional(),
  start_row: RowLabelOptionalSchema,
  finish_row: RowLabelOptionalSchema,
  task_status: z.string().max(100).nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

export const IrrigationTaskCreateSchema = IrrigationTaskSchema.omit({
  id: true,
  company_id: true,
  created_at: true,
  updated_at: true,
}).extend({
  is_manual_entry: z.number().int().nullable().optional(),
  block_id: z.number().int().nullable().optional(),
  count: z.number().int().nullable().optional(),
  week: z.number().int().nullable().optional(),
  labour_id: z.string().nullable().optional(),
  // irrigation_template.assigned_user is an INTEGER column holding a legacy
  // numeric user id (249, 205), NOT the uuid that spray_template.assigned_user
  // stores. The row therefore comes back to the client as a NUMBER, the edit
  // dialog round-trips it untouched, and a plain z.string() rejected every save
  // of a task that had ever been assigned — the "[object Object]" banner.
  // Accept both shapes; PostgREST casts a numeric string into the column fine.
  assigned_user: z.union([z.string(), z.number()]).nullable().optional(),
  // NOTE: the column is INTEGER, not varchar. Left as a string because that's what
  // the irrigation form posts and Postgres coerces a numeric string fine; switching
  // to z.number() would reject those posts. Every live row is NULL today.
  line_row_number: z.string().max(100).nullable().optional(),
  pack_category: z.string().max(250).nullable().optional(),
  pack_type: z.string().max(250).nullable().optional(),
  volume_used: z.number().nullable().optional(),
  // batch_number is the task's WHOLE batch selection, not one batch number — the
  // editor joins each product's batch, and migrated rows hold the JSON
  // [{"id":…,"batch_no":"…"}] shape (already 55 chars for two products, so four
  // would have breached the old cap of 100). The column is text.
  batch_number: z.string().max(4000).nullable().optional(),
  fertilizer_rate: z.string().max(150).nullable().optional(),
  // Column is varchar(11).
  wind_speed_saved: z.string().max(11).nullable().optional(),
  staff_count: z.number().int().nullable().optional(),
  labour_cost: z.number().nullable().optional(),
  total_cost: z.number().nullable().optional(),
  planting_reference: z.string().max(100).nullable().optional(),
  plants_per_hectre: z.number().nullable().optional(),
});

export const IrrigationTaskUpdateSchema = IrrigationTaskCreateSchema.partial();

export const IrrigationTaskListQuerySchema = z.object({
  search: z.string().optional(),
  block_type: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  per_page: z.coerce.number().int().positive().max(100).optional().default(25),
});

export type IrrigationTask = z.infer<typeof IrrigationTaskSchema>;
export type IrrigationTaskCreate = z.infer<typeof IrrigationTaskCreateSchema>;
export type IrrigationTaskUpdate = z.infer<typeof IrrigationTaskUpdateSchema>;
export type IrrigationTaskListQuery = z.infer<typeof IrrigationTaskListQuerySchema>;
